import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { api } from '../lib/api';
import { useToast } from '../context/ToastContext';

// Escuta de grupos — revisão da leitura (só superadmin). O wl-node captura as mensagens dos grupos marcados em
// "escutar" e lê cada uma por dois motores (regex e Gemini); aqui o superadmin marca ✓/✗ no que cada motor
// acertou. Serve para MEDIR e afinar a leitura — nenhuma resposta é enviada a ninguém nesta fase.
const FILTROS = [
  ['todas', 'Todas'],
  ['pedidos', 'Pedidos'],
  ['ofertas', 'Ofertas'],
  ['divergentes', 'Divergentes'],
  ['sem_avaliacao', 'Sem avaliação'],
];
const TIPOS_COR = { pedido: 'primary', oferta: 'success', resposta: 'info', outro: 'secondary' };
const ATUALIZA_A_CADA_MS = 30000;

// +55 19 99999-8888 a partir de 5519999998888 (DDI 55 e DDD só quando o tamanho confere; senão devolve com +)
function formatarTelefone(n) {
  const d = String(n || '').replace(/\D/g, '');
  const m = /^55(\d{2})(\d{4,5})(\d{4})$/.exec(d);
  return m ? `+55 ${m[1]} ${m[2]}-${m[3]}` : d ? `+${d}` : '';
}
const ehPedido = (i) => [i.gemini, i.regex, i.conversa?.gemini, i.conversa?.regex].some((l) => l && l.tipoMensagem === 'pedido');

// Simulação do que seria mandado no privado a quem pediu. Só mostra; nada é enviado.
function RespostaSimulada({ id, ok, por, onAvaliar, aoCarregar }) {
  const toast = useToast();
  const [aberta, setAberta] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [r, setR] = useState(null);
  const [modelo, setModelo] = useState('1'); // modelo do texto: 1, 2, 3 ou auto
  const [variacao, setVariacao] = useState(0);
  const ultimaReq = useRef(0); // cliques rápidos em "Outra variação"/modelo: só a última resposta vale

  async function carregar(modeloEscolhido, variacaoEscolhida) {
    const req = ++ultimaReq.current;
    setCarregando(true);
    try {
      const resposta = await api.get(`/escuta-grupos/${id}/resposta?modelo=${modeloEscolhido}&variacao=${variacaoEscolhida}`);
      if (req !== ultimaReq.current) return;
      setR(resposta);
      // o servidor zera a marca ✓/✗ quando a decisão muda: recarrega a lista para a tela não mostrar uma marca velha
      if (aoCarregar) aoCarregar();
    } catch (err) {
      if (req !== ultimaReq.current) return;
      toast.erro(err.message || 'Não foi possível simular a resposta');
      setAberta(false);
    } finally {
      if (req === ultimaReq.current) setCarregando(false);
    }
  }

  function abrir() {
    if (aberta) { setAberta(false); return; }
    setAberta(true);
    carregar(modelo, variacao);
  }
  function trocarModelo(m) { setModelo(m); setVariacao(0); carregar(m, 0); }
  function outraVariacao() { const v = variacao + 1; setVariacao(v); carregar(modelo, v); }

  return (
    <div className="mt-2">
      <Button size="sm" variant={aberta ? 'secondary' : 'outline-success'} onClick={abrir}>{aberta ? 'Esconder resposta simulada' : 'Ver resposta simulada'}</Button>
      {aberta && (
        <div className="border rounded p-2 mt-2 bg-light small">
          {carregando && <Spinner size="sm" />}
          {r && !r.aplicavel && <div className="text-muted">{r.motivo}</div>}
          {r && r.aplicavel && (
            <>
              <div className="mb-1">
                <Badge bg={r.acao === 'enviaria' ? 'success' : 'secondary'} className="me-2">{r.acao === 'enviaria' ? 'enviaria' : 'não enviaria'}</Badge>
                {r.motivo}
              </div>
              {onAvaliar && (
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="text-muted">A decisão acima (enviar ou não, e quais imóveis) estava certa?</span>
                  {ok !== null && ok !== undefined && por === 'claude' && <Badge bg="info" title="Marcado pelo Claude a pedido do Jason; clique em ✓ ou ✗ para você decidir">Claude</Badge>}
                  <ButtonGroup size="sm">
                    <Button variant={ok === true ? 'success' : 'outline-secondary'} title="Decisão certa" onClick={() => onAvaliar(ok === true ? null : true)}>✓</Button>
                    <Button variant={ok === false ? 'danger' : 'outline-secondary'} title="Decisão errada" onClick={() => onAvaliar(ok === false ? null : false)}>✗</Button>
                  </ButtonGroup>
                </div>
              )}
              <div className="text-muted mb-1">
                Lido por: <b>{r.leituraUsada}</b> · Procurando: {r.criterios.join(' · ') || 'sem critérios'} · {r.totalEncontrados} de {r.catalogo} imóveis do catálogo{r.empresa ? ` (${r.empresa})` : ''} atendem
              </div>
              {r.observacoes.map((o) => <div key={o} className="text-warning-emphasis">{o}</div>)}
              {r.juiz && (
                <div className="border rounded p-2 mt-2 bg-light">
                  {r.juiz.usado ? (
                    <>
                      <div className="fw-semibold mb-1">Gemini conferiu os {r.totalNoCatalogo} candidatos do catálogo:</div>
                      {r.juiz.veredictos.map((v) => (
                        <div key={v.id}>
                          <Badge bg={v.veredito === 'serve' ? 'success' : v.veredito === 'nao_serve' ? 'danger' : 'warning'} text={v.veredito === 'nao_sei' ? 'dark' : undefined} className="me-2">
                            {v.veredito === 'serve' ? 'serve' : v.veredito === 'nao_serve' ? 'não serve' : 'não sei'}
                          </Badge>
                          #{v.id} {v.titulo} — <span className="text-muted">{v.motivo}</span>
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className="text-warning-emphasis">O Gemini não pôde conferir ({r.juiz.erro}); vale só o casamento do catálogo.</div>
                  )}
                </div>
              )}
              {r.mensagem && (
                <>
                  <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                    <span className="fw-semibold">Mensagem no privado (simulação, nada foi enviado):</span>
                    <Form.Select size="sm" style={{ width: 'auto' }} value={modelo} onChange={(e) => trocarModelo(e.target.value)} aria-label="Modelo do texto">
                      {r.modelos.map((m) => <option key={m.id} value={m.id}>Modelo {m.id} — {m.nome}</option>)}
                      <option value="auto">Automático (varia o modelo)</option>
                    </Form.Select>
                    <Button size="sm" variant="outline-secondary" onClick={outraVariacao} title="Troca a frase de abertura e de fecho dentro do mesmo modelo">Outra variação</Button>
                  </div>
                  <div className="bg-white border rounded p-2" style={{ whiteSpace: 'pre-wrap' }}>{r.mensagem}</div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

const sim = (v) => (v ? 'sim' : 'não');
const reais = (v) => `R$ ${Number(v).toLocaleString('pt-BR')}`;
// um pedido da lista de pedidos, em uma linha
function resumoExtra(p) {
  const partes = [p.tipoImovel, p.dormitorios ? `${p.dormitorios} dorms` : null, p.valorMin ? `de ${reais(p.valorMin)}` : null, p.valorMax ? `até ${reais(p.valorMax)}` : null,
    p.bairros?.length ? `em ${p.bairros.join(' ou ')}` : null, p.cidades?.length ? p.cidades.join(' ou ') : null,
    p.bairrosExcluir?.length ? `sem ${p.bairrosExcluir.join(', ')}` : null, p.pagamento].filter(Boolean);
  return partes.join(', ') || 'sem detalhes';
}
function pares(l, dif) {
  const lista = [
    ['finalidade', 'finalidade', l.finalidade],
    ['imóvel', 'tipoImovel', l.tipoImovel],
    ['dorms', 'dormitorios', l.dormitorios],
    ['suítes', 'suites', l.suites],
    ['vagas', 'garagens', l.garagens],
    ['banheiros', 'banheiros', l.banheiros],
    ['área mín.', 'areaMin', l.areaMin],
    ['mín. R$', 'valorMin', l.valorMin],
    ['máx. R$', 'valorMax', l.valorMax],
    ['valor aprox. R$', 'valorAproximado', l.valorAproximado],
    ['bairros', 'bairros', l.bairros],
    ['cidades', 'cidades', l.cidades],
    ['SEM bairros', 'bairrosExcluir', l.bairrosExcluir],
    ['SEM cidades', 'cidadesExcluir', l.cidadesExcluir],
    ['pagamento', 'pagamento', l.pagamento],
    ['oferece permuta', 'oferecePermuta', typeof l.oferecePermuta === 'boolean' ? sim(l.oferecePermuta) : l.oferecePermuta],
    ['permuta de', 'permutaTipos', l.permutaTipos],
    ['dono aceita financ.', 'aceitaFinanciamento', typeof l.aceitaFinanciamento === 'boolean' ? sim(l.aceitaFinanciamento) : l.aceitaFinanciamento],
    ['dono aceita permuta', 'aceitaPermuta', typeof l.aceitaPermuta === 'boolean' ? sim(l.aceitaPermuta) : l.aceitaPermuta],
    ['condomínio/edifício', 'condominios', l.condominios],
    ['em condomínio', 'emCondominio', typeof l.emCondominio === 'boolean' ? sim(l.emCondominio) : l.emCondominio],
    ['só preferência', 'flexiveis', l.flexiveis],
    ['obs.', 'observacoes', l.observacoes],
    ['pedidos a mais', 'pedidosExtras', (l.pedidosExtras || []).map((p, k) => `${k + 2}º: ${resumoExtra(p)}`)],
    ['regex em dúvida', 'duvidas', (l.duvidas || []).map((d) => (d === 'varios_valores' ? 'vários valores (talvez lista de pedidos)' : d))],
  ];
  return lista
    .filter(([, , v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && !v.length))
    .map(([rotulo, campo, v]) => ({ rotulo, destaque: dif.includes(campo), valor: Array.isArray(v) ? v.join(', ') : String(v) }));
}

function Leitura({ rotulo, leitura, erro, naoLeu, divergencias, ok, por, extra, onAvaliar }) {
  return (
    <div className="d-flex align-items-start gap-2 py-1 border-top">
      <div style={{ width: 78, flex: '0 0 78px' }} className="fw-semibold small text-primary">{rotulo}</div>
      <div className="flex-grow-1 small">
        {erro ? (
          <em className="text-danger">erro: {erro}</em>
        ) : !leitura ? (
          <em className="text-muted">{naoLeu ? 'não enviada à IA (texto curto demais): não conta no placar' : 'ainda não lida'}</em>
        ) : (
          <>
            <Badge bg={TIPOS_COR[leitura.tipoMensagem] || 'secondary'} className="me-2">{leitura.tipoMensagem}</Badge>
            {pares(leitura, divergencias).map((p) => (
              <span key={p.rotulo} className={`me-3 d-inline-block ${p.destaque ? 'bg-warning-subtle rounded px-1' : ''}`}>
                {p.rotulo}: <b>{p.valor}</b>
              </span>
            ))}
          </>
        )}
        {extra}
      </div>
      {ok !== null && ok !== undefined && por === 'claude' && <Badge bg="info" className="align-self-center" title="Marcado pelo Claude a pedido do Jason; clique em ✓ ou ✗ para você decidir">Claude</Badge>}
      {onAvaliar && (
        <ButtonGroup size="sm" style={{ flex: '0 0 auto' }}>
          <Button variant={ok === true ? 'success' : 'outline-secondary'} title="Acertou" onClick={() => onAvaliar(ok === true ? null : true)}>✓</Button>
          <Button variant={ok === false ? 'danger' : 'outline-secondary'} title="Errou" onClick={() => onAvaliar(ok === false ? null : false)}>✗</Button>
        </ButtonGroup>
      )}
    </div>
  );
}

export default function EscutaGrupos() {
  const toast = useToast();
  const [carregando, setCarregando] = useState(true);
  const [dados, setDados] = useState(null);
  const [filtro, setFiltro] = useState('todas');
  const [contaId, setContaId] = useState('');
  const [grupo, setGrupo] = useState('');
  const [limite, setLimite] = useState(50);
  const [automatico, setAutomatico] = useState(true);
  const ultimaBusca = useRef(0);

  const carregar = useCallback(
    (silencioso = false) => {
      const busca = ++ultimaBusca.current;
      if (!silencioso) setCarregando(true);
      const q = new URLSearchParams({ filtro, limite });
      if (contaId) q.set('contaId', contaId);
      if (grupo) q.set('grupo', grupo);
      return api
        .get(`/escuta-grupos?${q}`)
        .then((r) => { if (busca === ultimaBusca.current) setDados(r); })
        .catch((err) => { if (!silencioso) toast.erro(err.message || 'Não foi possível carregar a escuta'); })
        .finally(() => { if (busca === ultimaBusca.current) setCarregando(false); });
    },
    [filtro, contaId, grupo, limite] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    if (!automatico) return undefined;
    const t = setInterval(() => { if (!document.hidden) carregar(true); }, ATUALIZA_A_CADA_MS);
    return () => clearInterval(t);
  }, [automatico, carregar]);

  async function avaliar(item, motor, ok) {
    const antes = item.ok[motor];
    const atualiza = (valor) => setDados((d) => ({ ...d, itens: d.itens.map((i) => (i.id === item.id ? { ...i, ok: { ...i.ok, [motor]: valor } } : i)) }));
    atualiza(ok);
    try {
      await api.put(`/escuta-grupos/${item.id}/avaliacao`, { motor, ok });
      carregar(true); // atualiza o placar de acerto
    } catch (err) {
      atualiza(antes);
      toast.erro(err.message || 'Não consegui salvar a avaliação');
    }
  }

  const r = dados?.resumo;
  const acerto = (m) => (r.acertoPct[m] === null ? '—' : `${r.acertoPct[m]}%`);
  const quando = (v) => new Date(v).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
        <h1 className="h4 mb-0">Escuta de grupos</h1>
        <Form.Check type="switch" id="escuta-auto" label="Atualizar sozinho (30 s)" checked={automatico} onChange={(e) => setAutomatico(e.target.checked)} />
      </div>
      <Alert variant="info" className="py-2 small">
        Fase de aprendizado: o sistema só <b>lê</b> as mensagens dos grupos marcados em "escutar" e nunca responde. Marque <b>✓</b> quando o motor entendeu
        certo e <b>✗</b> quando errou — o placar mostra qual motor acerta mais. Campos em amarelo são onde os motores divergem do regex.
      </Alert>

      {r && (
        <Row className="g-2 mb-3">
          {[
            ['Mensagens únicas', r.mensagens, `+ ${r.repetidas} repetidas em outros grupos`],
            ['Sem avaliação', r.semAvaliacao, r.ultima ? `última: ${quando(r.ultima)}` : 'nada capturado ainda'],
            ['Pedidos (regex / Gemini)', `${r.pedidos.regex} / ${r.pedidos.gemini}`, `ofertas: ${r.ofertas.regex} / ${r.ofertas.gemini}`],
            ['Acerto regex', acerto('regex'), `${r.avaliadas.regex} avaliadas`],
            ['Acerto da resposta simulada', acerto('resposta'), `${r.avaliadas.resposta} avaliadas`],
            ['Acerto Gemini', acerto('gemini'), `${r.avaliadas.gemini} avaliadas${r.geminiErros ? ` · ${r.geminiErros} erros` : ''}`],
          ].map(([titulo, valor, sub]) => (
            <Col key={titulo} xs={6} md={4} lg>
              <Card body className="py-2 h-100">
                <div className="small text-muted">{titulo}</div>
                <div className="fs-5 fw-bold">{valor}</div>
                <div className="small text-muted">{sub}</div>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
        <ButtonGroup size="sm">
          {FILTROS.map(([valor, rotulo]) => (
            <Button key={valor} variant={filtro === valor ? 'primary' : 'outline-primary'} onClick={() => { setFiltro(valor); setLimite(50); }}>{rotulo}</Button>
          ))}
        </ButtonGroup>
        {dados?.contas?.length > 1 && (
          <Form.Select size="sm" style={{ width: 'auto' }} value={contaId} onChange={(e) => { setContaId(e.target.value); setGrupo(''); setLimite(50); }}>
            <option value="">Todas as contas</option>
            {dados.contas.map((c) => <option key={c.id} value={c.id}>{c.nome} ({c.mensagens})</option>)}
          </Form.Select>
        )}
        <Form.Select size="sm" style={{ width: 'auto', maxWidth: 320 }} value={grupo} onChange={(e) => { setGrupo(e.target.value); setLimite(50); }}>
          <option value="">Todos os grupos</option>
          {(dados?.grupos || []).map((g) => <option key={g.id} value={g.id}>{g.nome} ({g.mensagens})</option>)}
        </Form.Select>
        {carregando && <Spinner size="sm" />}
      </div>

      {!carregando && dados && !dados.itens.length && <Alert variant="secondary">Nenhuma mensagem neste filtro.</Alert>}

      {dados?.itens.map((i) => (
        <Card key={i.id} body className="mb-2 py-2">
          <div className="small text-muted mb-1">
            {i.grupo} · {quando(i.recebidaEm)}
            {(i.remetenteNome || i.remetente) && <> · <b>{i.remetenteNome || 'sem nome'}</b>{i.remetente ? ` (${formatarTelefone(i.remetente)})` : ''}</>}
            {i.repeticoes > 0 && <> · <b>repetida {i.repeticoes}×</b>, também em: {i.tambemEm.join(', ')}</>}
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }} className="mb-2">{i.texto}</div>
          <Leitura rotulo="Regex" leitura={i.regex} divergencias={[]} ok={i.ok.regex} por={i.okPor?.regex} onAvaliar={(ok) => avaliar(i, 'regex', ok)} />
          <Leitura rotulo="Gemini" leitura={i.gemini} erro={i.geminiErro} naoLeu={i.geminiNaoLeu} divergencias={i.divergenciasGemini} ok={i.ok.gemini} por={i.okPor?.gemini} onAvaliar={(ok) => avaliar(i, 'gemini', ok)} />
          {i.conversa && (
            <div className="border rounded p-2 mt-2 bg-light small">
              <div className="fw-semibold mb-1">Pedido em pedaços: {i.conversa.ids.length} mensagens seguidas desta pessoa, lidas juntas</div>
              <div style={{ whiteSpace: 'pre-wrap' }} className="mb-1 text-muted">{i.conversa.texto}</div>
              <Leitura rotulo="Regex" leitura={i.conversa.regex} divergencias={[]} />
              <Leitura rotulo="Gemini" leitura={i.conversa.gemini} erro={i.conversa.erro} divergencias={[]} />
            </div>
          )}
          {ehPedido(i) && <RespostaSimulada id={i.id} ok={i.ok.resposta} por={i.okPor?.resposta} onAvaliar={(ok) => avaliar(i, 'resposta', ok)} aoCarregar={() => carregar(true)} />}
        </Card>
      ))}

      {dados && dados.itens.length >= limite && (
        <div className="text-center my-3">
          <Button variant="outline-secondary" size="sm" onClick={() => setLimite((l) => l + 50)}>Mostrar mais</Button>
        </div>
      )}
    </div>
  );
}
